/**
 * ScadenzePage — Admin backoffice scadenze contrattuali cross-tenant.
 *
 * Mostra tutte le pratiche attive con la loro data di scadenza calcolata
 * (dataStipula + durataMesi). Visibile solo ad ADMIN.
 *
 * Filtri: all (default) | upcoming (future) | past (scadute).
 * Ordinamento: giorni alla scadenza ASC (più urgenti in cima).
 */
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertCircle, Loader2 } from 'lucide-react';
import { AppLayout } from '@/components/layout/AppLayout';
import { RequireAuth } from '@/features/auth/components/RequireAuth';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { fetchScadenze, AdminApiError } from '../lib/admin-client';
import type { ScadenzaItem } from '@app/shared';

type FilterValue = 'all' | 'upcoming' | 'past';

const FILTER_LABELS: Record<FilterValue, string> = {
  all: 'Tutte',
  upcoming: 'Future',
  past: 'Scadute',
};

function giorniBadge(giorni: number) {
  if (giorni > 90)
    return <Badge variant="outline">{giorni} gg</Badge>;
  if (giorni > 30)
    return <Badge className="bg-yellow-100 text-yellow-800 border-yellow-300">{giorni} gg</Badge>;
  if (giorni > 0)
    return <Badge className="bg-orange-100 text-orange-800 border-orange-300">{giorni} gg</Badge>;
  return (
    <Badge className="bg-red-100 text-red-800 border-red-300">
      {Math.abs(giorni)} gg fa
    </Badge>
  );
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('it-IT', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

function ScadenzeTable({ items }: { items: ScadenzaItem[] }) {
  if (items.length === 0) {
    return (
      <div className="rounded-md border bg-muted/20 py-12 text-center text-sm text-muted-foreground">
        Nessuna scadenza trovata per il filtro selezionato.
      </div>
    );
  }

  return (
    <div className="overflow-x-auto rounded-md border">
      <table className="w-full text-sm">
        <thead className="bg-muted/40">
          <tr>
            <th className="px-4 py-3 text-left font-medium">Pratica</th>
            <th className="px-4 py-3 text-left font-medium">Tipologia</th>
            <th className="px-4 py-3 text-left font-medium">Stato</th>
            <th className="px-4 py-3 text-left font-medium">Data stipula</th>
            <th className="px-4 py-3 text-left font-medium">Durata</th>
            <th className="px-4 py-3 text-left font-medium">Scadenza</th>
            <th className="px-4 py-3 text-left font-medium">Alla scadenza</th>
          </tr>
        </thead>
        <tbody className="divide-y">
          {items.map((item) => (
            <tr key={item.praticaId} className="hover:bg-muted/20 transition-colors">
              <td className="px-4 py-3 font-mono text-xs">
                <Link
                  to={`/app/archivio/${item.praticaId}`}
                  className="text-primary underline-offset-4 hover:underline"
                >
                  {item.praticaId.slice(0, 8)}…
                </Link>
              </td>
              <td className="px-4 py-3">{item.tipologia}</td>
              <td className="px-4 py-3">
                <Badge variant="outline">{item.stato}</Badge>
              </td>
              <td className="px-4 py-3">{formatDate(item.dataStipula)}</td>
              <td className="px-4 py-3">{item.durataMesi} mesi</td>
              <td className="px-4 py-3">{formatDate(item.dataScadenza)}</td>
              <td className="px-4 py-3">{giorniBadge(item.giorniAllaScadenza)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ScadenzeContent() {
  const [filter, setFilter] = useState<FilterValue>('all');
  const [items, setItems] = useState<ScadenzaItem[]>([]);
  const [totale, setTotale] = useState(0);
  const [status, setStatus] = useState<'idle' | 'loading' | 'ready' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const load = useCallback(
    async (f: FilterValue) => {
      setStatus('loading');
      try {
        const data = await fetchScadenze(f);
        setItems(data.items);
        setTotale(data.totale);
        setStatus('ready');
      } catch (err) {
        const msg =
          err instanceof AdminApiError ? err.message : 'Errore durante il caricamento.';
        setErrorMsg(msg);
        setStatus('error');
      }
    },
    [],
  );

  useEffect(() => {
    void load(filter);
  }, [filter, load]);

  return (
    <div className="space-y-4">
      <header className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Scadenze contrattuali</h1>
          <p className="text-sm text-muted-foreground">
            Tutte le pratiche attive ordinate per data di scadenza contrattuale.
          </p>
        </div>

        <div className="flex gap-2">
          {(['all', 'upcoming', 'past'] as FilterValue[]).map((f) => (
            <Button
              key={f}
              variant={filter === f ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilter(f)}
            >
              {FILTER_LABELS[f]}
            </Button>
          ))}
        </div>
      </header>

      {status === 'loading' && (
        <div
          className="flex items-center justify-center rounded-md border bg-muted/20 py-12 text-sm text-muted-foreground"
          role="status"
          aria-live="polite"
        >
          <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
          Caricamento scadenze…
        </div>
      )}

      {status === 'error' && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" aria-hidden="true" />
          <AlertTitle>Errore</AlertTitle>
          <AlertDescription className="space-y-2">
            <p>{errorMsg}</p>
            <Button variant="outline" size="sm" onClick={() => void load(filter)}>
              Riprova
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {status === 'ready' && (
        <>
          <p className="text-sm text-muted-foreground">
            {totale} {totale === 1 ? 'scadenza' : 'scadenze'} trovate
          </p>
          <ScadenzeTable items={items} />
        </>
      )}
    </div>
  );
}

export function ScadenzePage() {
  return (
    <RequireAuth roles={['ADMIN']}>
      <AppLayout>
        <ScadenzeContent />
      </AppLayout>
    </RequireAuth>
  );
}
